import * as THREE from '/assets/scripts/three.module.js';
import { resolveEffectiveCaseState } from '../simulator/structured-state.js?v=20260817-relbridge9';
import { buildConstraintSolutions } from './constraint-solver.js?v=20260817-pcieanchor10';
import { primaryComponentList, primaryComponentsBySlot } from '../simulator/component-slots.js';
import { psuInstallLength } from './geometry.js';

function finite(value, fallback = 0) { const n = Number(value); return Number.isFinite(n) ? n : fallback; }

function attachedFanSegments(activeBoxes, radiator) {
  if (!radiator?.uid) return [];
  const bySlot=new Map();
  for (const fan of activeBoxes) {
    if (fan?.trackType!=='fan' || fan.attachedToRadiatorUid!==radiator.uid) continue;
    const slot=Number(fan.assignedSlotIndex);
    if (!Number.isInteger(slot)) continue;
    const record={
      index:slot,
      frame:finite(fan.nativeSpecs?.l || fan.nativeSpecs?.w,0),
      thickness:finite(fan.nativeSpecs?.h,0),
      manual:!fan.autoFabricatedFan
    };
    const existing=bySlot.get(slot);
    // A manual replacement owns the slot if an old auto placeholder is still disappearing
    // during the same UI transaction.
    if (!existing || record.manual) bySlot.set(slot,record);
  }
  return [...bySlot.values()].sort((a,b)=>a.index-b.index);
}

function runtimeAnchorFor(comp, activeBoxes) {
  if (comp?.trackType!=='radiator' || !comp?.spatialUserAdjusted) return null;
  const caseBox=activeBoxes.find(item=>item?.isCase);
  if (!caseBox || typeof comp.getWorldPosition!=='function' || typeof caseBox.getWorldPosition!=='function') return null;
  const world=comp.getWorldPosition(new THREE.Vector3());
  const caseWorld=caseBox.getWorldPosition(new THREE.Vector3());
  const local=world.sub(caseWorld);
  return {
    // `locked` here means fixed for this deterministic solve, not that the UI Lock button
    // must be engaged. A translated AIO is explicit user spatial intent and dependent
    // hardware should solve around it on an inventory change.
    locked:true,
    uiLocked:!!comp.isLockedToCase,
    position:[local.x,local.y,local.z],
    orientationClass:Number.isInteger(Number(comp.relationalOrientationClass))?Number(comp.relationalOrientationClass):0,
    mount:comp.activeMount || null,
    source:comp.isLockedToCase?'user_locked_runtime':'user_adjusted_runtime'
  };
}

export function componentSelections(activeBoxes) {
  const slots = primaryComponentsBySlot(activeBoxes);
  const mobo = slots.get('motherboard');
  const psu = slots.get('psu');
  const gpu = slots.get('gpu');
  const cooling = slots.get('cooling');
  const cooler = cooling?.trackType === 'aircooler' ? cooling : null;
  const rad = cooling?.trackType === 'radiator' ? cooling : null;
  const fanSegments=rad ? attachedFanSegments(activeBoxes,rad) : [];
  const manualSegments=fanSegments.filter(segment=>segment.manual);
  const maxFanThickness=fanSegments.length?Math.max(...fanSegments.map(segment=>finite(segment.thickness,0)),0):0;
  return {
    mobo: mobo?.componentMeta?.formFactor || '',
    psu: psu?.componentMeta?.formFactor || '',
    psuL: psu ? psuInstallLength(psu.nativeSpecs, psu.componentMeta?.formFactor) : 0,
    cooling: rad ? 'aio' : (cooler ? 'air' : ''),
    rad: rad?.componentMeta?.radiatorSize || 0,
    radFan: rad ? (manualSegments.length ? 'manual' : (rad?.componentMeta?.radFan || rad?.componentMeta?.fanProfile || 'standard')) : '',
    fanThickness: rad ? (maxFanThickness || finite(rad?.componentMeta?.fanThickness,25)) : 0,
    fanSegments: manualSegments.length ? fanSegments : [],
    gpuL: finite(gpu?.nativeSpecs?.l), gpuT: finite(gpu?.nativeSpecs?.w), gpuH: finite(gpu?.nativeSpecs?.h),
    cpuH: finite(cooler?.nativeSpecs?.h)
  };
}

function componentDescriptors(activeBoxes) {
  return primaryComponentList(activeBoxes).map(comp => {
    const fanSegments=comp.trackType==='radiator' ? attachedFanSegments(activeBoxes,comp) : [];
    const manualSegments=fanSegments.filter(segment=>segment.manual);
    return ({
    type: comp.trackType,
    actual: true,
    uid: comp.uid || '',
    formFactor: comp.componentMeta?.formFactor || '',
    radiatorSize: comp.componentMeta?.radiatorSize || 0,
    radFan: comp.trackType==='radiator' ? (manualSegments.length ? 'manual' : (comp.componentMeta?.radFan || comp.componentMeta?.fanProfile || 'standard')) : '',
    fanThickness: comp.trackType==='radiator' ? (fanSegments.length ? Math.max(...fanSegments.map(segment=>finite(segment.thickness,0)),0) : finite(comp.componentMeta?.fanThickness,25)) : 0,
    fanSegments: comp.trackType==='radiator' && manualSegments.length ? fanSegments : [],
    anchor: runtimeAnchorFor(comp,activeBoxes),
    fanSize: comp.trackType==='radiator' ? finite(comp.componentMeta?.fanSize,0) : 0,
    fanCount: comp.trackType==='radiator' ? finite(comp.componentMeta?.fanCount,0) : 0,
    dimensionKnown: comp.componentMeta?.dimensionKnown || null,
    dimensionSource: comp.componentMeta?.dimensionSource || null,
    dims: {
      l: finite(comp.nativeSpecs?.l),
      w: finite(comp.nativeSpecs?.w),
      h: finite(comp.nativeSpecs?.h)
    }
  });
  });
}

function convertCandidate(candidate) {
  return candidate ? { ...candidate, pos:new THREE.Vector3(...candidate.position) } : null;
}
function convertCandidateMap(source = {}) {
  const out={};
  for (const type of ['motherboard','gpu','psu','radiator']) out[type]=(source?.[type]||[]).map(convertCandidate).filter(Boolean);
  return out;
}
function convertEnvelopeMap(source = {}) {
  const out={};
  for(const [key,value] of Object.entries(source||{})) out[key]={pos:new THREE.Vector3(...value.position),size:new THREE.Vector3(...value.size)};
  return out;
}

export function buildSpatialPlan(caseObj, overlay, spatialState, activeBoxes = []) {
  const selections = componentSelections(activeBoxes);
  const actualEffective = resolveEffectiveCaseState(caseObj, overlay, spatialState, selections);
  // Structural placement deliberately ignores selected GPU/cooler/PSU dimensions. Logical
  // identities (motherboard form factor, PSU type, cooling branch, radiator size/fan rule)
  // still matter, but a shorter installed GPU must never create fake motherboard travel.
  const structuralSelections = {
    mobo:selections.mobo,
    psu:selections.psu,
    cooling:selections.cooling,
    rad:selections.rad,
    radFan:selections.radFan,
    fanThickness:selections.fanThickness,
    fanSegments:selections.fanSegments
  };
  const structuralEffective = resolveEffectiveCaseState(caseObj, overlay, spatialState, structuralSelections);
  structuralEffective.validationLimits = { ...(actualEffective?.limits || structuralEffective?.limits || {}) };
  structuralEffective.actualMatchedRules = actualEffective?.matchedRules || [];
  structuralEffective.actualMatchedRuleFailures = actualEffective?.matchedRuleFailures || [];
  structuralEffective.actualMatchedRuleWarnings = actualEffective?.matchedRuleWarnings || [];
  const solved = buildConstraintSolutions({
    caseObj,
    overlay,
    effective:structuralEffective,
    components: componentDescriptors(activeBoxes)
  });
  if (!solved) return null;

  return {
    schemaVersion: 7,
    layoutFamily: solved.best.family,
    layoutVariant: solved.best.variant,
    topologyId: solved.best.id,
    topologyConfidence: solved.confidence,
    placementConfidence: solved.placementConfidence ?? solved.confidence,
    topologyScore: solved.best.score,
    topologyReasons: solved.best.reasons,
    topologyAlternatives: solved.alternatives,
    transformClass: solved.best.transformClass,
    effective: solved.effective || effective,
    candidates:convertCandidateMap(solved.candidates),
    envelopes:convertEnvelopeMap(solved.envelopes),
    spineX: solved.best.spineX ?? null,
    solver: solved,
    capabilityProfile: solved.capabilityProfile || null,
    configurationBranch: solved.configurationBranch || null,
    configurationBranches: solved.configurationBranches || [],
    structuralReservations: solved.reservations || null,
    placementSelectionSource:'deterministic_fallback',
    hasHardValidSolution:!!solved.hasHardValidSolution,
    hardDiagnostics:solved.hardDiagnostics || null,
    componentValidity:solved.componentValidity || {},
    gnnRanking:null
  };
}

export function applyRankCandidateToPlan(plan, rankCandidate, rankingMeta = null) {
  if(!plan||!rankCandidate||!rankCandidate.hardValid)return plan;
  return {
    ...plan,
    layoutFamily:rankCandidate.family,
    layoutVariant:rankCandidate.variant,
    topologyId:rankCandidate.topologyId,
    topologyScore:rankCandidate.deterministicScore,
    topologyReasons:rankCandidate.reasons||[],
    transformClass:rankCandidate.transformClass,
    candidates:convertCandidateMap(rankCandidate.candidates),
    envelopes:convertEnvelopeMap(rankCandidate.envelopes),
    spineX:rankCandidate.spineX??null,
    placementSelectionSource:'relational_gnn_ranker',
    hasHardValidSolution:true,
    hardDiagnostics:rankCandidate.hardDiagnostics || null,
    componentValidity:rankCandidate.componentValidity || {},
    structuralReservations:rankCandidate.reservations || plan.structuralReservations || null,
    gnnRanking:rankingMeta?{...rankingMeta,candidateId:rankCandidate.rankId}: {candidateId:rankCandidate.rankId}
  };
}

export function componentPlacementStatus(plan, comp) {
  if (!plan || !comp) return { valid:false, reason:'missing_plan' };
  const record = plan.componentValidity?.[comp.trackType];
  if (record && record.valid === false) {
    return { valid:false, reason:(record.reasons || []).join(' | ') || 'hard_validation_failed', reasons:[...(record.reasons || [])] };
  }
  if (comp.trackType === 'aircooler') return { valid:true, reason:'motherboard_socket' };
  const candidates = plan.candidates?.[comp.trackType] || [];
  if (!candidates.length) return { valid:false, reason:'no_mount_candidate', reasons:['no_mount_candidate'] };
  return { valid:true, reason:'hard_valid_candidate' };
}

export function validSnapCandidates(plan, comp) {
  if (!plan || !comp || comp.trackType === 'aircooler') return [];
  const status = componentPlacementStatus(plan, comp);
  if (!status.valid) return [];
  return [...(plan.candidates?.[comp.trackType] || [])].sort((a,b) => (b.priority||0)-(a.priority||0));
}

export function chooseSnapCandidate(plan, comp) {
  const valid = validSnapCandidates(plan, comp);
  if (!valid.length) return null;
  if (comp.preferredSnapId) {
    const preferred = valid.find(c => c.id === comp.preferredSnapId);
    if (preferred) return preferred;
  }
  // Orientation and mount position are one atomic solver candidate. A manual orientation
  // preference may select another hard-valid candidate, but it must never be applied to the
  // position of a candidate that was solved for a different orientation.
  if (Number.isInteger(comp.preferredOrientationClass)) {
    const sameOrientation = valid.find(c => Number(c.orientationClass) === Number(comp.preferredOrientationClass));
    if (sameOrientation) return sameOrientation;
  }
  return valid[0];
}

export function cycleSnapCandidate(plan, comp, direction = 1) {
  const valid = validSnapCandidates(plan, comp);
  if (!valid.length) return null;
  let idx = valid.findIndex(c => c.id === comp.preferredSnapId);
  if (idx < 0) idx = 0;
  idx = (idx + (direction >= 0 ? 1 : -1) + valid.length) % valid.length;
  comp.preferredSnapId = valid[idx].id;
  return valid[idx];
}
